import { useEffect, useState } from "react";
import { ShieldCheck, KeyRound, AlertCircle, Copy, Check, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Admin → Beveiliging → 2FA setup / backup codes / disable.
 *
 * The status ("enabled" / "not enabled") is read from `/me/2fa/status`
 * so we don't rely on the user document that AuthContext caches.
 */
export default function SecurityAdmin() {
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState(null); // { enabled, activated_at, backup_codes_left }
  const [setup, setSetup] = useState(null);    // { secret, otpauth_uri, qr_data_url }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [disableCode, setDisableCode] = useState("");

  const load = () => {
    api.get("/me/2fa/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Setup mislukt");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/confirm", { code: code.trim() });
      setBackupCodes(data.codes);
      setSetup(null);
      setCode("");
      await checkAuth();
      load();
      toast.success("2FA ingeschakeld — bewaar je backup-codes op een veilige plek!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code klopt niet");
    } finally {
      setBusy(false);
    }
  };

  const disable2fa = async () => {
    if (!window.confirm("Weet je zeker dat je 2FA wilt uitschakelen? Je account is dan minder goed beveiligd.")) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode.trim() });
      setDisableCode("");
      await checkAuth();
      load();
      toast.success("2FA uitgeschakeld");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Uitschakelen mislukt");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async (c, i) => {
    try {
      await navigator.clipboard.writeText(c);
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch { /* ignore */ }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      toast.success("Alle backup-codes gekopieerd");
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6" data-testid="admin-security-panel">
      <div>
        <p className="ed-eyebrow">Beveiliging</p>
        <h2 className="font-serif text-3xl mt-2 leading-tight">Tweefactor-authenticatie (2FA)</h2>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl leading-relaxed">
          Voor extra bescherming bij admin-login vragen we naast je wachtwoord een 6-cijferige code uit een authenticator-app (Google Authenticator, Authy, 1Password, Bitwarden). Werkt zonder telefoonnetwerk.
        </p>
      </div>

      {/* Status card */}
      {status && (
        <div className="ed-card p-6" data-testid="admin-2fa-status">
          {status.enabled ? (
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-emerald-700 text-[#F9F8F6] flex items-center justify-center shrink-0">
                <ShieldCheck size={18} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg leading-tight">2FA staat aan</p>
                <p className="text-xs text-neutral-600 mt-1">
                  Ingeschakeld op {status.activated_at ? new Date(status.activated_at).toLocaleDateString("nl-NL") : "?"} · {status.backup_codes_left ?? 0} backup-codes over
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
                <AlertCircle size={18} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg leading-tight">2FA staat uit</p>
                <p className="text-xs text-neutral-600 mt-1">
                  Aanbevolen voor alle admin-accounts. Duurt 1 minuut om in te stellen.
                </p>
              </div>
              <button
                onClick={startSetup}
                disabled={busy || !!setup}
                data-testid="admin-2fa-start"
                className="ed-btn shrink-0 inline-flex items-center gap-2 disabled:opacity-50"
              >
                <KeyRound size={12} strokeWidth={1.75} /> 2FA inschakelen
              </button>
            </div>
          )}
        </div>
      )}

      {/* Setup wizard */}
      {setup && (
        <div className="ed-card p-6" data-testid="admin-2fa-setup">
          <p className="ed-eyebrow">Stap 1 · Scan de QR</p>
          <h3 className="font-serif text-xl mt-2">Open je authenticator-app en scan deze QR</h3>
          <div className="mt-4 flex flex-wrap gap-8 items-start">
            <img
              src={setup.qr_data_url}
              alt="2FA QR"
              data-testid="admin-2fa-qr"
              style={{ width: 200, height: 200, imageRendering: "pixelated" }}
              className="border border-neutral-200"
            />
            <div className="flex-1 min-w-[16rem]">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">Handmatig invoeren?</p>
              <p className="mt-2 font-mono text-sm break-all bg-neutral-50 border border-neutral-200 p-3">
                {setup.secret}
              </p>
              <form onSubmit={confirmSetup} className="mt-5">
                <label className="block">
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 block mb-1.5">
                    Stap 2 · Vul de 6-cijferige code in
                  </span>
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    data-testid="admin-2fa-confirm-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full border border-neutral-300 px-3 py-3 text-center font-mono text-2xl tracking-[0.5em] focus:border-neutral-900 outline-none"
                    placeholder="000000"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  data-testid="admin-2fa-confirm"
                  className="mt-4 w-full ed-btn bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-50"
                >
                  {busy ? "Bevestigen…" : "Bevestig & schakel 2FA in"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Backup codes after activation */}
      {backupCodes && (
        <div className="ed-card p-6 border-amber-500 border" data-testid="admin-2fa-backup-codes">
          <p className="ed-eyebrow text-amber-800">Bewaar deze backup-codes ⚠️</p>
          <h3 className="font-serif text-xl mt-2">Belangrijk: bewaar deze codes veilig</h3>
          <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
            Deze codes zijn je enige manier om in te loggen als je je telefoon kwijtraakt. Ze zijn <strong>eenmalig bruikbaar</strong> en we tonen ze <strong>maar één keer</strong>.
          </p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-sm">
            {backupCodes.map((c, i) => (
              <button
                key={i}
                onClick={() => copyCode(c, i)}
                data-testid={`admin-2fa-code-${i}`}
                className="bg-neutral-50 border border-neutral-200 hover:border-neutral-900 px-3 py-2.5 inline-flex items-center justify-between gap-1.5 transition-colors"
              >
                <span>{c}</span>
                {copiedIdx === i ? <Check size={11} strokeWidth={2} /> : <Copy size={11} strokeWidth={1.5} className="opacity-40" />}
              </button>
            ))}
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={copyAll} className="ed-btn ed-btn-outline inline-flex items-center gap-2">
              <Copy size={12} strokeWidth={1.75} /> Kopieer alle codes
            </button>
            <button
              onClick={() => setBackupCodes(null)}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] px-3 py-2 hover:bg-neutral-100"
            >
              Ik heb ze bewaard — verberg
            </button>
          </div>
        </div>
      )}

      {/* Disable 2FA — only when enabled */}
      {status?.enabled && !setup && (
        <div className="ed-card p-6" data-testid="admin-2fa-disable-card">
          <p className="ed-eyebrow">2FA uitschakelen</p>
          <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
            Vul een geldige code uit je authenticator-app in om 2FA uit te schakelen.
          </p>
          <div className="mt-4 flex flex-wrap gap-3 items-end">
            <input
              type="text"
              maxLength={6}
              inputMode="numeric"
              data-testid="admin-2fa-disable-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
              className="border border-neutral-300 px-3 py-2 font-mono text-center tracking-[0.3em] focus:border-neutral-900 outline-none"
              placeholder="000000"
              style={{ width: "10rem" }}
            />
            <button
              onClick={disable2fa}
              disabled={busy || disableCode.length !== 6}
              data-testid="admin-2fa-disable-btn"
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-red-600 text-red-700 hover:bg-red-50 px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <ShieldOff size={12} strokeWidth={1.75} /> 2FA uitschakelen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
